import { useCallback, useEffect, useMemo, useState } from 'react';
import { packages as packagesApi, services as servicesApi, stores as storesApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { Loading, Empty, Modal, Can, money } from '../components/Common';

/** Turnaround presets, matching how the catalogue is set up. */
const TURNAROUND_CHOICES = [
  { hours: 24, label: '24 hours (1 day)' },
  { hours: 48, label: '48 hours (2 days)' },
  { hours: 72, label: '72 hours (3 days)' },
  { hours: 96, label: '96 hours (4 days)' },
  { hours: 120, label: '120 hours (5 days)' },
];

const ICON_CHOICES = ['📦', '👔', '🧺', '👕', '✨', '💼', '🏠', '🎽', '👗', '🧥', '⚡', '💎'];

const turnaroundLabel = (hours) => {
  if (!hours) return '—';
  const days = hours / 24;
  return Number.isInteger(days) ? `${days} day${days === 1 ? '' : 's'}` : `${hours} hours`;
};

/**
 * Packages & combos.
 *
 * A package is a fixed selection of services sold at one price. The Super Admin
 * creates platform-wide packages; an outlet manager creates ones scoped to its
 * own outlet. The catalogue total and the saving are computed by the server, so
 * a package can never advertise a discount that isn't real.
 */
export default function Packages() {
  const toast = useToast();
  const { isSuperAdmin } = useAuth();

  const [list, setList] = useState([]);
  const [items, setItems] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [packs, catalogue, stores] = await Promise.all([
        packagesApi.list(),
        servicesApi.items(),
        isSuperAdmin ? storesApi.list({ limit: 100 }) : Promise.resolve({ data: { stores: [] } }),
      ]);
      setList(packs.data.packages);
      setItems(catalogue.data.items);
      setStoreList(stores.data.stores);
    } catch (err) {
      toast.error('Could not load packages', err.message);
    } finally {
      setLoading(false);
    }
  }, [toast, isSuperAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'Packages · UrbanClean'; }, []);

  const toggle = async (pack) => {
    try {
      const res = await packagesApi.toggle(pack._id);
      toast.success(res.message);
      load();
    } catch (err) {
      toast.error('Could not update', err.message);
    }
  };

  const remove = async (pack) => {
    if (!window.confirm(`Delete the package "${pack.name}"?`)) return;
    try {
      await packagesApi.remove(pack._id);
      toast.success('Package deleted');
      load();
    } catch (err) {
      toast.error('Could not delete', err.message);
    }
  };

  if (loading) return <Loading label="Loading packages" />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Packages &amp; Combos</h1>
          <div className="sub">
            Bundle services at a fixed price. {isSuperAdmin ? 'Yours apply everywhere unless pinned to an outlet.' : 'Yours apply to your outlet.'}
          </div>
        </div>
        <Can permission="package.create">
          <button className="btn btn-blue" onClick={() => setEditing({})}>+ New package</button>
        </Can>
      </div>

      {list.length === 0 ? (
        <div className="card">
          <Empty
            title="No packages yet"
            body="Create a combo — pick two or more services, set one price, and it appears in the app."
          />
        </div>
      ) : (
        <div className="grid grid-3">
          {list.map((pack) => (
            <div className="card" key={pack._id} style={{ opacity: pack.isActive ? 1 : 0.7 }}>
              <div className="card-body">
                <div style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: 52, height: 52, borderRadius: 13, flexShrink: 0, fontSize: 26,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: `${pack.color || '#3182C8'}1A`,
                    }}
                  >
                    {pack.icon || '📦'}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="strong" style={{ fontSize: 15, color: 'var(--navy)' }}>{pack.name}</div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                      {pack.description || `${pack.items.length} services`}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 8 }}>
                      <span className="strong" style={{ fontSize: 19, color: 'var(--navy)' }}>{money(pack.price)}</span>
                      {pack.savings > 0 ? (
                        <>
                          <span className="muted" style={{ textDecoration: 'line-through', fontSize: 13 }}>
                            {money(pack.originalPrice)}
                          </span>
                          <span className="badge badge-green">save {pack.savingsPercent}%</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 12, paddingTop: 11, borderTop: '1px solid var(--line)' }}>
                  {pack.items.map((line, i) => (
                    <div key={i} className="muted" style={{ fontSize: 12.5, display: 'flex', justifyContent: 'space-between' }}>
                      <span>{line.item?.name || 'Removed service'}</span>
                      <span>× {line.quantity}</span>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 6, marginTop: 11, flexWrap: 'wrap' }}>
                  <span className="badge badge-blue">ready in {turnaroundLabel(pack.turnaroundHours)}</span>
                  <span className="badge badge-gray">{pack.store?.name || 'All outlets'}</span>
                  <span className={`badge badge-${pack.isActive ? 'green' : 'gray'}`}>
                    {pack.isActive ? 'Live' : 'Hidden'}
                  </span>
                  {pack.totalOrdered > 0 ? <span className="badge badge-amber">{pack.totalOrdered} ordered</span> : null}
                </div>

                <div style={{ display: 'flex', gap: 6, marginTop: 13, paddingTop: 11, borderTop: '1px solid var(--line)' }}>
                  <Can permission="package.update">
                    <button className="btn btn-sm btn-ghost" onClick={() => toggle(pack)}>
                      {pack.isActive ? 'Hide' : 'Publish'}
                    </button>
                    <button className="btn btn-sm btn-ghost" onClick={() => setEditing(pack)}>Edit</button>
                  </Can>
                  <Can permission="package.delete">
                    <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)' }} onClick={() => remove(pack)}>
                      Delete
                    </button>
                  </Can>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PackageEditor
        pack={editing}
        items={items}
        storeList={storeList}
        isSuperAdmin={isSuperAdmin}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </>
  );
}

const BLANK = {
  name: '', description: '', icon: '📦', color: '#3182C8',
  price: '', turnaroundHours: 72, store: '', sortOrder: 0, validTill: '',
};

function PackageEditor({ pack, items, storeList, isSuperAdmin, onClose, onSaved }) {
  const toast = useToast();

  const [form, setForm] = useState(BLANK);
  const [picked, setPicked] = useState({});   // itemId -> quantity
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pack) return;
    if (!pack._id) {
      setForm(BLANK);
      setPicked({});
      setPreview(null);
      return;
    }
    setForm({
      name: pack.name || '',
      description: pack.description || '',
      icon: pack.icon || '📦',
      color: pack.color || '#3182C8',
      price: pack.price ?? '',
      turnaroundHours: pack.turnaroundHours ?? 72,
      store: pack.store?._id || pack.store || '',
      sortOrder: pack.sortOrder ?? 0,
      validTill: pack.validTill ? String(pack.validTill).slice(0, 10) : '',
    });
    setPicked(
      (pack.items || []).reduce((acc, line) => {
        const id = line.item?._id || line.item;
        if (id) acc[id] = line.quantity;
        return acc;
      }, {})
    );
  }, [pack]);

  const chosen = useMemo(
    () => Object.entries(picked).map(([item, quantity]) => ({ item, quantity })),
    [picked]
  );

  // Ask the server for the catalogue total whenever the selection changes, so
  // the saving shown here is the same one the app will show.
  useEffect(() => {
    if (chosen.length < 2) { setPreview(null); return; }
    let cancelled = false;

    packagesApi
      .preview({ items: chosen, price: Number(form.price) || 0, store: form.store || undefined })
      .then((res) => { if (!cancelled) setPreview(res.data); })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [chosen, form.price, form.store]);

  if (!pack) return null;
  const isNew = !pack._id;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const setQuantity = (id, quantity) => {
    setPicked((p) => {
      const next = { ...p };
      if (quantity <= 0) delete next[id];
      else next[id] = quantity;
      return next;
    });
  };

  const visible = search
    ? items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()))
    : items;

  const save = async () => {
    if (!form.name.trim()) return toast.error('Give the package a name');
    if (chosen.length < 2) return toast.error('Pick at least two services');
    if (form.price === '' || Number(form.price) < 0) return toast.error('Set a package price');

    setBusy(true);
    try {
      const body = {
        ...form,
        price: Number(form.price),
        turnaroundHours: Number(form.turnaroundHours),
        sortOrder: Number(form.sortOrder) || 0,
        store: form.store || null,
        validTill: form.validTill || null,
        items: chosen,
      };
      if (pack._id) await packagesApi.update(pack._id, body);
      else await packagesApi.create(body);

      toast.success(isNew ? 'Package created' : 'Package updated');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      wide
      title={isNew ? 'New package' : `Edit — ${pack.name}`}
      onClose={onClose}
      footer={<>
        {preview ? (
          <span className="muted" style={{ marginRight: 'auto', fontSize: 12.5 }}>
            Catalogue {money(preview.originalPrice)} → package {money(Number(form.price) || 0)}
            {preview.savings > 0 ? ` · saves ${money(preview.savings)} (${preview.savingsPercent}%)` : ''}
          </span>
        ) : null}
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : isNew ? 'Create package' : 'Save changes'}
        </button>
      </>}
    >
      <div className="row">
        <div className="field">
          <label>Package name *</label>
          <input className="input" value={form.name} onChange={set('name')} placeholder="Office Combo" />
        </div>
        <div className="field">
          <label>Icon</label>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {ICON_CHOICES.map((icon) => (
              <button
                key={icon}
                type="button"
                onClick={() => setForm((f) => ({ ...f, icon }))}
                style={{
                  width: 34, height: 34, fontSize: 17, cursor: 'pointer', borderRadius: 7,
                  background: form.icon === icon ? 'var(--blue-100)' : 'var(--surface)',
                  border: `1.5px solid ${form.icon === icon ? 'var(--blue)' : 'var(--line)'}`,
                }}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="field">
        <label>Description</label>
        <input className="input" value={form.description} onChange={set('description')} placeholder="3 shirts, 2 trousers and a suit" />
      </div>

      {/* Service picker */}
      <div className="field">
        <label>Services in this package * <span className="muted">(at least two)</span></label>
        <input
          className="input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search the catalogue…"
          style={{ marginBottom: 10 }}
        />

        <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
          <table className="data">
            <thead>
              <tr>
                <th>Service</th><th>Ready in</th>
                <th className="right">Rate</th><th className="center" style={{ width: 130 }}>Qty</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => (
                <tr key={item._id} style={{ background: picked[item._id] ? 'var(--blue-50)' : undefined }}>
                  <td>
                    {item.name}
                    <div className="muted" style={{ fontSize: 11.5 }}>{item.category?.name} · per {item.unit}</div>
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{turnaroundLabel(item.turnaroundHours)}</td>
                  <td className="right">{money(item.price)}</td>
                  <td className="center">
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                      <button className="btn btn-sm btn-ghost" onClick={() => setQuantity(item._id, (picked[item._id] || 0) - 1)}>−</button>
                      <span style={{ minWidth: 18 }}>{picked[item._id] || 0}</span>
                      <button className="btn btn-sm btn-ghost" onClick={() => setQuantity(item._id, (picked[item._id] || 0) + 1)}>+</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Live preview from the server */}
      {preview ? (
        <div className="card" style={{ background: 'var(--blue-50)', border: 'none', boxShadow: 'none', marginBottom: 16 }}>
          <div className="card-body" style={{ padding: 14 }}>
            {preview.items.map((line, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                <span>{line.name} × {line.quantity}</span>
                <span className="muted">{money(line.total)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--line)', paddingTop: 8, marginTop: 8 }}>
              <span className="strong">Buying separately</span>
              <span className="strong">{money(preview.originalPrice)}</span>
            </div>
            <div className="hint" style={{ marginTop: 6 }}>
              Slowest service takes {turnaroundLabel(preview.turnaroundHours)} — the package cannot be ready sooner.
            </div>
          </div>
        </div>
      ) : null}

      <div className="row">
        <div className="field">
          <label>Package price *</label>
          <input className="input" type="number" value={form.price} onChange={set('price')} placeholder="699" />
          {preview && Number(form.price) > preview.originalPrice ? (
            <div className="hint" style={{ color: 'var(--amber)' }}>
              This is more than buying the items separately.
            </div>
          ) : null}
        </div>
        <div className="field">
          <label>Ready in</label>
          <select className="select" value={form.turnaroundHours} onChange={set('turnaroundHours')}>
            {TURNAROUND_CHOICES.map((t) => <option key={t.hours} value={t.hours}>{t.label}</option>)}
          </select>
          <div className="hint">Shown to the customer in the app.</div>
        </div>
      </div>

      <div className="row">
        {isSuperAdmin && storeList.length ? (
          <div className="field">
            <label>Available at</label>
            <select className="select" value={form.store} onChange={set('store')}>
              <option value="">All outlets</option>
              {storeList.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
          </div>
        ) : null}
        <div className="field">
          <label>Valid till</label>
          <input className="input" type="date" value={form.validTill} onChange={set('validTill')} />
          <div className="hint">Leave blank for no expiry.</div>
        </div>
        <div className="field">
          <label>Sort order</label>
          <input className="input" type="number" value={form.sortOrder} onChange={set('sortOrder')} />
        </div>
      </div>
    </Modal>
  );
}
